/**
 * HEASStrategyAllocator.tsx – Home Equity Access Scheme details (payment amount, frequency, equity impact).
 * Used by AgedCareAnalyser in App.js. Props: factFind, details, setDetails, legacyYears (optional).
 */
import React, { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { Info, ChevronDown, TrendingDown, TrendingUp } from 'lucide-react';
import { formatCurrency } from '../../utils/index.ts';
import * as calculationApi from '../../services/calculationApi.ts';
import { PENSION_RATES } from '../../constants/index.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';

export interface HEASDetails {
    paymentAmount?: number | string;
    paymentFrequency?: 'fortnightly' | 'monthly';
    interestRate?: number | string;
}

interface HEASStrategyAllocatorProps {
    factFind: DraftFactFind;
    details: HEASDetails;
    setDetails: Dispatch<SetStateAction<HEASDetails>>;
    legacyYears?: number;
}

function HEASStrategyAllocator({ factFind, details, setDetails, legacyYears: legacyYearsProp }: HEASStrategyAllocatorProps) {
    const [isEducationExpanded, setIsEducationExpanded] = useState(false);
    const legacyYears = legacyYearsProp ?? 5;

    const handleDetailChange = (field: keyof HEASDetails, value: string) => {
        if (field === 'paymentAmount') {
            const num = value === '' || value === undefined ? 0 : Number(value);
            setDetails(prev => ({ ...prev, paymentAmount: Number.isFinite(num) ? Math.max(0, num) : 0 }));
        } else {
            setDetails(prev => ({ ...prev, [field]: value }));
        }
    };

    const [isOnAgePension, setIsOnAgePension] = useState(false);
    useEffect(() => {
        let cancelled = false;
        calculationApi.calculateFinancials(factFind).then((financials) => {
            if (!cancelled) setIsOnAgePension((Number(financials.socialSecurity) || 0) > 0);
        });
        return () => { cancelled = true; };
    }, [factFind]);
    const relationshipStatus = factFind.adviceFor === 'couple' ? 'couple' : 'single';
    const maxPensionRate = PENSION_RATES[relationshipStatus] || PENSION_RATES.single;
    const maxHEASPayment = maxPensionRate * 1.5;
    const maxHEASPaymentFortnightly = maxPensionRate / 26 * 1.5;

    const annualPaymentAmount = details.paymentFrequency === 'fortnightly'
        ? (Number(details.paymentAmount) || 0) * 26
        : (Number(details.paymentAmount) || 0) * 12;

    const homeValue = parseFloat(String(factFind.home?.value)) || 0;
    const homeGrowthRate = parseFloat(String(factFind.home?.homeGrowthRate)) || 5;
    const interestRate = parseFloat(String(details.interestRate)) || 3.95;

    let projectedHomeValueWithHEAS = homeValue;
    let projectedHEASLoanBalance = 0;

    for (let year = 1; year <= legacyYears; year++) {
        projectedHomeValueWithHEAS = projectedHomeValueWithHEAS * (1 + homeGrowthRate / 100);
        projectedHEASLoanBalance = projectedHEASLoanBalance * (1 + interestRate / 100);
        projectedHEASLoanBalance += annualPaymentAmount;
    }

    let projectedHomeValueNoHEAS = homeValue;
    for (let year = 1; year <= legacyYears; year++) {
        projectedHomeValueNoHEAS = projectedHomeValueNoHEAS * (1 + homeGrowthRate / 100);
    }

    const projectedEquityWithHEAS = Math.max(0, projectedHomeValueWithHEAS - projectedHEASLoanBalance);
    const projectedEquityNoHEAS = projectedHomeValueNoHEAS;
    const equityDifference = projectedEquityNoHEAS - projectedEquityWithHEAS;
    const equityPercentageOwned = annualPaymentAmount > 0 ? (projectedEquityWithHEAS / projectedHomeValueWithHEAS * 100) : 100;

    return (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border space-y-4">
            <h4 className="font-semibold text-lg">Home Equity Access Scheme (HEAS) Details</h4>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                    onClick={() => setIsEducationExpanded(!isEducationExpanded)}
                    className="w-full p-4 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 transition-all duration-200 flex items-center justify-between"
                >
                    <h5 className="font-semibold text-gray-900 flex items-center gap-2">
                        <Info className="w-5 h-5 text-blue-600" />
                        What is HEAS?
                    </h5>
                    {isEducationExpanded ? (
                        <ChevronDown className="w-5 h-5 text-gray-600 transform rotate-180 transition-transform" />
                    ) : (
                        <ChevronDown className="w-5 h-5 text-gray-600 transition-transform" />
                    )}
                </button>
                {isEducationExpanded && (
                    <div className="p-4 bg-white border-t border-gray-200 space-y-3">
                        <div className="text-sm text-gray-700 space-y-3">
                            <p>
                                The <strong>Home Equity Access Scheme (HEAS)</strong> is a government program that allows Age Pension recipients to access equity in their home
                                through a loan scheme. Unlike commercial reverse mortgages, HEAS is administered by Services Australia (Centrelink) and offers a lower interest rate
                                (currently {details.interestRate}% p.a.) and specific eligibility requirements.
                            </p>
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="font-semibold text-blue-900 mb-2">Key Differences from Commercial Reverse Mortgages:</p>
                                <ul className="text-xs text-gray-700 space-y-2 list-disc list-inside">
                                    <li><strong>Lower interest rate:</strong> HEAS charges {details.interestRate}% p.a. (government-set), compared to typically 6-8%+ for commercial reverse mortgages.</li>
                                    <li><strong>Age Pension requirement:</strong> You must be receiving or eligible for Age Pension to access HEAS.</li>
                                    <li><strong>Dollar-for-dollar reduction:</strong> HEAS payments reduce your Age Pension dollar-for-dollar, meaning you receive the payment but your pension is reduced by the same amount.</li>
                                    <li><strong>Payment flexibility:</strong> You can start and stop HEAS payments as needed, providing more control over your cash flow.</li>
                                    <li><strong>Maximum payment:</strong> HEAS is limited to 150% of the maximum Age Pension rate (currently {formatCurrency(maxHEASPayment)} per year).</li>
                                    <li><strong>Government-backed:</strong> HEAS is administered by Services Australia, providing security and transparency.</li>
                                </ul>
                            </div>
                            <p>
                                <strong>How it works:</strong> You receive regular payments (fortnightly or monthly) from the government, which are added to a loan secured against your home.
                                The loan balance compounds at {details.interestRate}% p.a. and reduces your home equity over time. Your Age Pension is reduced by the exact amount of each payment,
                                so you're essentially borrowing against your home equity rather than receiving additional income.
                            </p>
                            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="font-semibold text-blue-900 mb-2">Impact on Age Pension:</p>
                                <p className="text-xs text-gray-700 mb-2">
                                    HEAS payments reduce your Age Pension dollar-for-dollar{annualPaymentAmount > 0 ? `. This means if you receive ${formatCurrency(annualPaymentAmount)} per year from HEAS, your Age Pension will be reduced by ${formatCurrency(annualPaymentAmount)} per year.` : ''} While this may seem counterintuitive, HEAS can still be beneficial because:
                                </p>
                                <ul className="text-xs text-gray-700 space-y-1 list-disc list-inside">
                                    <li>You receive the payment immediately (rather than waiting for pension payments)</li>
                                    <li>The loan balance grows at a lower rate ({details.interestRate}% p.a.) than many commercial options</li>
                                    <li>You maintain control over when and how much you draw</li>
                                    <li>HEAS payments are not assessable for Age Pension means test (they're treated as a loan, not income)</li>
                                </ul>
                            </div>
                            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="font-semibold text-blue-900 mb-2">Impact on Aged Care Assessment:</p>
                                <p className="text-xs text-gray-700 mb-2">
                                    HEAS payments are treated as assessable income for aged care cost calculations:
                                </p>
                                <ul className="text-xs text-gray-700 list-disc list-inside space-y-1 mb-2">
                                    <li><strong>HSC (Hotelling Supplement Contribution):</strong> HEAS payments increase your assessable income, which may increase your HSC contribution.
                                    The HSC is calculated based on your Means Tested Amount (MTA), which considers both income and assets.</li>
                                    <li><strong>NCCC (Needs-Based Care Contribution):</strong> Similar to HSC, HEAS payments as assessable income may increase your NCCC,
                                    as it's also calculated based on your MTA.</li>
                                </ul>
                                <p className="text-xs text-gray-700">
                                    <strong>Note:</strong> The HEAS loan balance itself is a liability secured against your home, but the regular payments you receive are treated as income for means testing purposes.
                                </p>
                            </div>
                            <p>
                                <strong>Key considerations:</strong> HEAS can provide access to home equity for Age Pension recipients, but it reduces your home equity over time through compound interest.
                                The loan must be repaid when the property is sold or you move permanently. You can start and stop payments as needed, providing flexibility in managing your cash flow.
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {!isOnAgePension && (
                <div className="p-3 bg-red-50 border-l-4 border-red-400 rounded-r-lg text-sm">
                    <p className="text-red-800 font-semibold">⚠️ Eligibility Requirement: You must be receiving Age Pension or be eligible for Age Pension to access HEAS.</p>
                </div>
            )}

            <div className="p-3 bg-blue-50 border-l-4 border-blue-400 rounded-r-lg text-sm">
                <p><strong className="text-blue-700">Maximum Available:</strong> {formatCurrency(maxHEASPaymentFortnightly)} per fortnight ({formatCurrency(maxHEASPayment)} per year) - 150% of maximum Age Pension rate</p>
                <p className="mt-2"><strong className="text-blue-700">Interest Rate:</strong> {details.interestRate}% p.a. (compound) - Government-set rate</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Amount ($)</label>
                    <input
                        type="number"
                        value={details.paymentAmount || ''}
                        onChange={(e) => handleDetailChange('paymentAmount', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0"
                        max={details.paymentFrequency === 'fortnightly' ? maxHEASPaymentFortnightly : maxHEASPayment / 12}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        Max: {formatCurrency(details.paymentFrequency === 'fortnightly' ? maxHEASPaymentFortnightly : maxHEASPayment / 12)} per {details.paymentFrequency === 'fortnightly' ? 'fortnight' : 'month'}
                    </p>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Frequency</label>
                    <select
                        value={details.paymentFrequency || 'fortnightly'}
                        onChange={(e) => handleDetailChange('paymentFrequency', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        <option value="fortnightly">Fortnightly</option>
                        <option value="monthly">Monthly</option>
                    </select>
                </div>
            </div>

            <div className="p-3 bg-green-50 border-l-4 border-green-400 rounded-r-lg text-sm">
                <p><strong className="text-green-700">Annual Payment:</strong> {formatCurrency(annualPaymentAmount)}</p>
                {isOnAgePension && (
                    <p className="mt-2"><strong className="text-green-700">Age Pension Impact:</strong> Your Age Pension will be reduced by {formatCurrency(annualPaymentAmount)} per year (dollar-for-dollar reduction)</p>
                )}
            </div>

            {annualPaymentAmount > 0 && (
                <div className="space-y-4">
                    <h5 className="font-semibold text-gray-900 text-lg">Impact on Home Equity</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg">
                            <h6 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                                <TrendingDown className="w-4 h-4" />
                                With HEAS
                            </h6>
                            <div className="mb-4">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm text-gray-700">Home Equity Ownership</span>
                                    <span className="text-lg font-bold text-blue-700">{equityPercentageOwned.toFixed(1)}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                                    <div
                                        className="bg-gradient-to-r from-blue-500 to-indigo-600 h-6 flex items-center justify-end pr-2 transition-all duration-500"
                                        style={{ width: `${Math.min(100, equityPercentageOwned)}%` }}
                                    >
                                        {equityPercentageOwned > 15 && (
                                            <span className="text-xs font-semibold text-white">{equityPercentageOwned.toFixed(1)}%</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Projected Home Value:</span>
                                    <span className="font-medium">{formatCurrency(projectedHomeValueWithHEAS)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">HEAS Loan Balance:</span>
                                    <span className="font-medium text-red-600">-{formatCurrency(projectedHEASLoanBalance)}</span>
                                </div>
                                <div className="flex justify-between pt-2 border-t border-blue-200">
                                    <span className="font-semibold text-gray-900">Your Equity:</span>
                                    <span className="font-bold text-blue-700">{formatCurrency(projectedEquityWithHEAS)}</span>
                                </div>
                            </div>
                        </div>
                        <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg">
                            <h6 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                                <TrendingUp className="w-4 h-4" />
                                Without HEAS
                            </h6>
                            <div className="mb-4">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm text-gray-700">Home Equity Ownership</span>
                                    <span className="text-lg font-bold text-green-700">100%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                                    <div className="bg-gradient-to-r from-green-500 to-emerald-600 h-6 flex items-center justify-end pr-2" style={{ width: '100%' }}>
                                        <span className="text-xs font-semibold text-white">100%</span>
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Projected Home Value:</span>
                                    <span className="font-medium">{formatCurrency(projectedHomeValueNoHEAS)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">HEAS Loan Balance:</span>
                                    <span className="font-medium text-gray-400">$0</span>
                                </div>
                                <div className="flex justify-between pt-2 border-t border-green-200">
                                    <span className="font-semibold text-gray-900">Your Equity:</span>
                                    <span className="font-bold text-green-700">{formatCurrency(projectedEquityNoHEAS)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="p-4 bg-gray-50 border border-gray-300 rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                            <span className="font-semibold text-gray-900">Equity Impact After {legacyYears} Years:</span>
                            <span className="text-lg font-bold text-red-600">-{formatCurrency(equityDifference)}</span>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">
                            By accessing HEAS, your home equity will be <strong>{formatCurrency(equityDifference)} less</strong> after {legacyYears} years compared to not accessing the scheme.
                        </p>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="text-gray-600">With HEAS:</span>
                                <span className="font-semibold text-blue-700 ml-2">{formatCurrency(projectedEquityWithHEAS)}</span>
                            </div>
                            <div>
                                <span className="text-gray-600">Without HEAS:</span>
                                <span className="font-semibold text-green-700 ml-2">{formatCurrency(projectedEquityNoHEAS)}</span>
                            </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-3">
                            Note: This projection assumes home growth at {homeGrowthRate}% p.a. and HEAS interest at {interestRate}% p.a.
                            Actual results may vary. Use the "View Projections" button for a detailed analysis including all assets.
                        </p>
                    </div>
                    <div className="p-4 bg-gray-50 border-l-4 border-gray-400 rounded-r-lg text-sm">
                        <p className="font-semibold text-gray-900 mb-2">Additional Considerations:</p>
                        <ul className="list-disc list-inside space-y-1 text-gray-700">
                            <li>HEAS payments reduce Age Pension dollar-for-dollar</li>
                            <li>Interest compounds at {details.interestRate}% p.a., reducing your estate value</li>
                            <li>Home must remain your principal residence</li>
                            <li>You can start and stop payments as needed</li>
                            <li>Lower interest rate than commercial reverse mortgages</li>
                            <li>HEAS payments are assessable income for aged care cost calculations (HSC and NCCC)</li>
                        </ul>
                    </div>
                </div>
            )}
        </div>
    );
}

export default HEASStrategyAllocator;
